import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Marker, Popup, useMap } from "react-leaflet";
import { groupBy } from "lodash";
import MarkerClusterGroup from "react-leaflet-cluster";
import { multipleRecordsIcon, clusterMarkersIcon } from "./MapIcons";

const MapContent = ({ markerPoints }) => {
  const map = useMap();

  const locationGroups = groupBy(markerPoints, "site_id");

  const locationGroupsArray = Object.values(locationGroups);

  useEffect(() => {
    if (locationGroupsArray.length > 0) {
      // Compute the geographic bounds of the markers and adjust the map view to contain all of them.
      // It uses the first position from each group since all positions in a group are the same.
      const bounds = locationGroupsArray.map(group => [
        group[0].lat,
        group[0].long
      ]);
      map.fitBounds(bounds, { padding: [25, 25] });
    }
  }, [markerPoints]);

  // Generate map markers with popup info.
  // useMemo recomputes markers only when 'markerPoints' changes
  const markers = useMemo(
    () =>
      locationGroupsArray.map(group => {
        const dates = group.map(record => new Date(record.date));

        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));

        const dateDisplay =
          group.length === 1
            ? minDate.toLocaleDateString()
            : `${minDate.toLocaleDateString()} - ${maxDate.toLocaleDateString()}`;

        return (
          <Marker
            key={`${group[0].site_id}-${group[0].lat}-${group[0].long}`}
            icon={multipleRecordsIcon(group.length)}
            position={[group[0].lat, group[0].long]}
            recordCount={group.length}
            opacity={0.7}
          >
            <Popup>
              <span>
                Site ID: {group[0].site_id}
                <br />
                Record Count: {group.length}
                <br />
                {group.length === 1 ? "Date: " : "Date Range: "}
                {dateDisplay}
              </span>
            </Popup>
          </Marker>
        );
      }),
    [locationGroupsArray]
  );

  return (
    <>
      <MarkerClusterGroup
        chunkedLoading
        iconCreateFunction={cluster => {
          // Get all child markers of the cluster
          const childMarkers = cluster.getAllChildMarkers();
          // Sum up the records from these child markers using the stored record count
          const totalRecords = childMarkers.reduce((acc, marker) => {
            return acc + marker.options.recordCount;
          }, 0);
          // Create the cluster icon using the total count
          return clusterMarkersIcon(totalRecords);
        }}
      >
        {markers}
      </MarkerClusterGroup>
    </>
  );
};

MapContent.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  markerPoints: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default MapContent;
